import { useEffect, useState } from 'react';
import { Shield, AlertCircle } from 'lucide-react';
import { Link, Navigate } from 'react-router-dom';
import { oauthApi } from '../lib/api';

export default function AuthCallback() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get('code');
      const errorParam = params.get('error');
      const state = params.get('state');

      if (errorParam) {
        setStatus('error');
        setError(`Authentication failed: ${errorParam}`);
        return;
      }

      if (!code) {
        setStatus('error');
        setError('No authorization code provided');
        return;
      }

      try {
        const response = await oauthApi.callback(code, state || '');

        if (response.success) {
          setStatus('success');
          setTimeout(() => {
            setIsRedirecting(true);
          }, 500);
        } else {
          setStatus('error');
          setError(response.message || 'Authentication failed');
        }
      } catch (err) {
        setStatus('error');
        const errorMessage =
          err instanceof Error ? err.message : 'An unknown error occurred';
        console.error('Authentication error:', errorMessage);
        setError(`Authentication failed: ${errorMessage}`);
      }
    };

    handleCallback();
  }, []);

  if (isRedirecting) {
    return <Navigate to="/settings" replace />;
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-pulse" />
          <h2 className="text-2xl font-bold text-gray-900">
            Processing Authentication
          </h2>
          <p className="mt-2 text-gray-600">
            Please wait while we complete your authentication...
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900">Authentication Error</h2>
          <p className="mt-2 text-gray-600">{error}</p>
          <Link
            to="/settings"
            className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Shield className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900">
          Authentication Successful
        </h2>
        <p className="mt-2 text-gray-600">Redirecting to settings...</p>
      </div>
    </div>
  );
}
